import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Video, Users, ClipboardList, Archive, Paperclip, X, ChevronDown, ChevronUp, FileCheck2, CalendarClock } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";

// iter62 — template categories (mirror /app/frontend/src/components/MeetingTemplatesTab.jsx)
const TEMPLATE_CATEGORIES = [
  { key: "safety",   label: "Safety",             color: "#e63946" },
  { key: "quality",  label: "Quality",            color: "#1a5fb4" },
  { key: "ops",      label: "Operations",         color: "#1a8754" },
  { key: "hr",       label: "HR / People",        color: "#a13ad6" },
  { key: "finance",  label: "Finance",            color: "#d6a900" },
  { key: "audit",    label: "Audit / Compliance", color: "#ff4500" },
  { key: "custom",   label: "Custom",             color: "#1a1a1a" },
];

export default function CreateMeetingDialog({ open, onClose, onCreated, prefill = null }) {
  const [styles, setStyles] = useState([]);
  const [style, setStyle] = useState("management_review");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [openActions, setOpenActions] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [selectedArchived, setSelectedArchived] = useState(new Set());
  const [vvItems, setVvItems] = useState([]);
  const [selectedVv, setSelectedVv] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [selectedAttendees, setSelectedAttendees] = useState(new Set());
  const [guestInvites, setGuestInvites] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [fileIds, setFileIds] = useState([]);
  // iter58 — scheduling + template library
  const [scheduledFor, setScheduledFor] = useState("");
  const [startNow, setStartNow] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  // iter58 — free-form agenda items (title + description)
  const [agendaItems, setAgendaItems] = useState([]);
  const [newAgendaTitle, setNewAgendaTitle] = useState("");
  const [newAgendaDesc, setNewAgendaDesc] = useState("");
  // iter62 — category filter for template picker
  const [templateCategory, setTemplateCategory] = useState("all");

  const defaultScheduled = () => {
    // Default to tomorrow 10:00 local
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  };

  useEffect(() => {
    if (!open) return;
    setTitle(prefill?.title || "");
    setStyle(prefill?.style || "management_review");
    setSelectedActions(new Set(prefill?.action_ids || []));
    setSelectedArchived(new Set(prefill?.archived_action_ids || []));
    setSelectedVv(new Set(prefill?.vv_item_ids || []));
    setSelectedAttendees(new Set(prefill?.attendee_user_ids || []));
    setGuestInvites(prefill?.guest_invites || []);
    setGuestName(""); setGuestEmail("");
    setFileIds([]);
    setScheduledFor(prefill?.scheduled_for?.slice(0, 16) || defaultScheduled());
    setStartNow(false);
    setSelectedTemplateId("");
    setAgendaItems(prefill?.agenda_items || []);
    setNewAgendaTitle(""); setNewAgendaDesc("");
    api.get("/formal-meetings/styles").then((r) => setStyles(r.data || [])).catch(() => {});
    api.get("/actions", { params: { include_archived: false } }).then((r) => setOpenActions(r.data || [])).catch(() => {});
    api.get("/vv-items").then((r) => setVvItems((r.data || []).filter((v) => v.status !== "completed"))).catch(() => {});
    api.get("/users/visible-to-me").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/meeting-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, [open, prefill]);

  // iter58 — load a template into the form
  const loadTemplate = (tid) => {
    setSelectedTemplateId(tid);
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setTitle(t.title || "");
    setStyle(t.style || "custom");
    setSelectedVv(new Set(t.default_vv_item_ids || []));
    toast.success(`Loaded "${t.title}" · edit anything, then create.`);
  };

  useEffect(() => {
    if (showArchived && archived.length === 0) {
      api.get("/actions", { params: { include_archived: true } })
        .then((r) => setArchived((r.data || []).filter((a) => a.archived)))
        .catch(() => {});
    }
  }, [showArchived, archived.length]);

  const styleLabel = useMemo(() => Object.fromEntries(styles.map((s) => [s.key, s.label])), [styles]);

  const toggleFrom = (set, setSet, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSet(next);
  };

  const sortedUsers = useMemo(() => [...users].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);

  const submit = async () => {
    if (!title.trim()) return toast.error("Meeting title required");
    setBusy(true);
    try {
      const r = await api.post("/formal-meetings", {
        title: title.trim(),
        style,
        action_ids: Array.from(selectedActions),
        archived_action_ids: Array.from(selectedArchived),
        vv_item_ids: Array.from(selectedVv),
        attendee_user_ids: Array.from(selectedAttendees),
        guest_invites: guestInvites,
        file_ids: fileIds,
        agenda_items: agendaItems,
        scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        start_now: startNow,
        template_id: selectedTemplateId || null,
      });
      onCreated?.(r.data);
      onClose();
      toast.success(startNow ? "Meeting started — attendees notified." : "Meeting scheduled — start it on the date.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create meeting");
    } finally { setBusy(false); }
  };

  const openActionsOnly = openActions.filter((a) => a.kind !== "task");
  const attCount = selectedAttendees.size;
  const actCount = selectedActions.size + selectedArchived.size;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[92vh] overflow-auto scrollbar-thin p-0">
        <div className="sticky top-0 bg-white border-b border-[#1a1a1a]/10 px-6 py-4 z-10">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
              <span className="w-8 h-8 rounded-xl bg-[#ff4500] text-white flex items-center justify-center"><Video className="w-4 h-4" /></span>
              Create a meeting
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* iter58 + iter62 — Load from library (grouped by category) */}
          {templates.length > 0 && (
            <div className="rounded-xl border-2 border-[#a13ad6]/25 bg-[#a13ad6]/06 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#a13ad6] font-bold mb-1.5">
                // Meeting Template Library — start from a prebuilt agenda
              </div>
              {/* iter62 — Category sub-tabs */}
              {(() => {
                const counts = { all: templates.length };
                templates.forEach((t) => {
                  const k = t.category || "custom";
                  counts[k] = (counts[k] || 0) + 1;
                });
                const filtered = templateCategory === "all"
                  ? templates
                  : templates.filter((t) => (t.category || "custom") === templateCategory);
                return (
                  <>
                    <div className="flex flex-wrap gap-1 mb-2" data-testid="mtg-template-cat-tabs">
                      <button
                        type="button"
                        data-testid="mtg-template-cat-all"
                        onClick={() => { setTemplateCategory("all"); setSelectedTemplateId(""); }}
                        className={`text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border transition-colors ${
                          templateCategory === "all"
                            ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                            : "bg-white text-[#1a1a1a]/70 border-[#1a1a1a]/15 hover:border-[#1a1a1a]/40"
                        }`}>
                        All ({counts.all})
                      </button>
                      {TEMPLATE_CATEGORIES.map((c) => {
                        const active = templateCategory === c.key;
                        const disabled = !counts[c.key];
                        return (
                          <button
                            key={c.key}
                            type="button"
                            data-testid={`mtg-template-cat-${c.key}`}
                            disabled={disabled}
                            onClick={() => { setTemplateCategory(c.key); setSelectedTemplateId(""); }}
                            className={`text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border transition-colors ${
                              active ? "text-white" : "bg-white text-[#1a1a1a]/70 border-[#1a1a1a]/15 hover:border-[#1a1a1a]/40"
                            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                            style={active ? { backgroundColor: c.color, borderColor: c.color } : {}}>
                            {c.label} ({counts[c.key] || 0})
                          </button>
                        );
                      })}
                    </div>
                    <select
                      data-testid="mtg-template-picker"
                      value={selectedTemplateId}
                      onChange={(e) => loadTemplate(e.target.value)}
                      className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                      <option value="">— Build from scratch —</option>
                      {filtered.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}{t.cloned_from_master_id ? " · from TruPath library" : ""} · {t.style?.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                    <div className="text-[10px] text-[#1a1a1a]/55 mt-1">
                      {filtered.length === 0
                        ? "No templates in this category yet."
                        : `Picking a template pre-fills title, style, and any attached V&V items. You can edit everything below before creating.`}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <Section label="Meeting title *">
            <input data-testid="mtg-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. July Monthly Management Review"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15" />
          </Section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section label="Meeting style">
              <select data-testid="mtg-style-input" value={style} onChange={(e) => setStyle(e.target.value)}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                {styles.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Section>
            <Section label="Scheduled for">
              <input
                data-testid="mtg-scheduled-input"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm"
              />
            </Section>
          </div>

          <label className="flex items-center gap-2 text-[12px] cursor-pointer" data-testid="mtg-start-now-toggle">
            <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} className="accent-[#ff4500]" />
            <span className="text-[#1a1a1a]">Start immediately — skip scheduling and go live now (fires attendee invite emails)</span>
          </label>

          <Section label={<span><ClipboardList className="w-3 h-3 inline mr-1" /> Actions to discuss ({selectedActions.size})</span>}>
            <div className="max-h-52 overflow-auto border border-[#1a1a1a]/10 rounded-xl bg-white">
              {openActionsOnly.length === 0 && <div className="text-[11px] text-[#1a1a1a]/55 p-3">No open actions yet.</div>}
              {openActionsOnly.map((a) => {
                const on = selectedActions.has(a.id);
                return (
                  <label key={a.id} data-testid={`mtg-action-${a.id}`} className={`flex items-start gap-2 p-2 border-b border-[#1a1a1a]/06 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleFrom(selectedActions, setSelectedActions, a.id)} className="accent-[#ff4500] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] font-bold text-[#ff4500]">{a.display_id}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60">{a.status}</span>
                        {a.priority && a.priority !== "normal" && <span className="text-[9px] uppercase text-white bg-[#ff4500] px-1.5 rounded-full">{a.priority}</span>}
                      </div>
                      <div className="text-[12px] font-medium text-[#1a1a1a] truncate">{a.title}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section label={<span><FileCheck2 className="w-3 h-3 inline mr-1" /> V&V items to review ({selectedVv.size})</span>}>
            <div className="max-h-52 overflow-auto border border-[#1a1a1a]/10 rounded-xl bg-white">
              {vvItems.length === 0 && <div className="text-[11px] text-[#1a1a1a]/55 p-3">No open V&amp;V items.</div>}
              {vvItems.map((v) => {
                const on = selectedVv.has(v.id);
                return (
                  <label key={v.id} data-testid={`mtg-vv-${v.id}`} className={`flex items-start gap-2 p-2 border-b border-[#1a1a1a]/06 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleFrom(selectedVv, setSelectedVv, v.id)} className="accent-[#a13ad6] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] font-bold text-[#a13ad6]">{v.display_id}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60">{v.status}</span>
                        {v.due_date && <span className="text-[9px] uppercase text-[#1a1a1a]/60 font-mono">due {String(v.due_date).slice(0, 10)}</span>}
                      </div>
                      <div className="text-[12px] font-medium text-[#1a1a1a] truncate">{v.title}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Section>

          {/* iter58 — Additional agenda items (free-form title + description) */}
          <Section label={<span><ClipboardList className="w-3 h-3 inline mr-1" /> Additional agenda items ({agendaItems.length})</span>}>
            {agendaItems.length > 0 && (
              <ul className="mb-2 space-y-1.5">
                {agendaItems.map((a, i) => (
                  <li key={i} data-testid={`mtg-agenda-${i}`}
                    className="flex items-start justify-between gap-2 p-2 rounded-xl bg-[#faf8f4] border border-[#1a1a1a]/08 text-[12px]">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#1a1a1a]">{a.title}</div>
                      {a.description && <div className="text-[11px] text-[#1a1a1a]/70 mt-0.5">{a.description}</div>}
                    </div>
                    <button type="button"
                      data-testid={`mtg-agenda-remove-${i}`}
                      onClick={() => setAgendaItems(agendaItems.filter((_, idx) => idx !== i))}
                      className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-1 gap-1.5 p-2 rounded-xl border border-dashed border-[#1a1a1a]/20 bg-white">
              <input
                data-testid="mtg-agenda-new-title"
                value={newAgendaTitle}
                onChange={(e) => setNewAgendaTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newAgendaTitle.trim()) {
                    e.preventDefault();
                    setAgendaItems([...agendaItems, { title: newAgendaTitle.trim(), description: newAgendaDesc.trim() }]);
                    setNewAgendaTitle(""); setNewAgendaDesc("");
                  }
                }}
                placeholder="Agenda title — e.g. Q3 pest control review"
                className="w-full border border-[#1a1a1a]/10 rounded-lg px-2 py-1.5 text-[12px] focus:outline-none focus:border-[#1a1a1a]/40"
              />
              <textarea
                data-testid="mtg-agenda-new-desc"
                value={newAgendaDesc}
                onChange={(e) => setNewAgendaDesc(e.target.value)}
                placeholder="Optional description or context…"
                rows={2}
                className="w-full border border-[#1a1a1a]/10 rounded-lg px-2 py-1.5 text-[11px] focus:outline-none focus:border-[#1a1a1a]/40 resize-none"
              />
              <button
                type="button"
                data-testid="mtg-agenda-add"
                disabled={!newAgendaTitle.trim()}
                onClick={() => {
                  setAgendaItems([...agendaItems, { title: newAgendaTitle.trim(), description: newAgendaDesc.trim() }]);
                  setNewAgendaTitle(""); setNewAgendaDesc("");
                }}
                className="btn-pill btn-pill-secondary text-[10px] self-start disabled:opacity-40"
              >
                + Add agenda item
              </button>
            </div>
          </Section>

          <div>
            <button data-testid="mtg-archive-toggle" onClick={() => setShowArchived((v) => !v)} type="button"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 hover:text-[#ff4500]">
              <Archive className="w-3 h-3" /> Closed archive {selectedArchived.size ? `· ${selectedArchived.size} selected` : ""}
              {showArchived ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showArchived && (
              <div className="mt-2 max-h-40 overflow-auto border border-[#1a1a1a]/10 rounded-xl bg-white" data-testid="mtg-archive-list">
                {archived.length === 0 && <div className="text-[11px] text-[#1a1a1a]/55 p-3">No archived actions yet.</div>}
                {archived.map((a) => {
                  const on = selectedArchived.has(a.id);
                  return (
                    <label key={a.id} className={`flex items-start gap-2 p-2 border-b border-[#1a1a1a]/06 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleFrom(selectedArchived, setSelectedArchived, a.id)} className="accent-[#ff4500] mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-[10px] font-bold text-[#1a1a1a]/70">{a.display_id}</span>
                        <span className="text-[11px] text-[#1a1a1a]/70 ml-1.5 truncate">{a.title}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Section label={<span><Users className="w-3 h-3 inline mr-1" /> Attendees ({attCount})</span>}>
            <div className="max-h-44 overflow-auto border border-[#1a1a1a]/10 rounded-xl bg-white">
              {sortedUsers.map((u) => {
                const on = selectedAttendees.has(u.id);
                return (
                  <label key={u.id} data-testid={`mtg-attendee-${u.id}`} className={`flex items-center gap-2 p-2 border-b border-[#1a1a1a]/06 hover:bg-[#faf8f4] cursor-pointer ${on ? "bg-[#fff1ea]" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleFrom(selectedAttendees, setSelectedAttendees, u.id)} className="accent-[#ff4500]" />
                    <span className="text-[12px] font-medium text-[#1a1a1a] flex-1">{u.name} <span className="text-[10px] text-[#1a1a1a]/50 ml-1">{u.email}</span></span>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section label={<span><Paperclip className="w-3 h-3 inline mr-1" /> Extra attachments</span>}>
            <PhotoUploader photoIds={fileIds} onChange={setFileIds} testIdPrefix="mtg-file" />
            <div className="text-[10px] text-[#1a1a1a]/55 mt-1">Files already attached to the selected actions will be pulled in automatically.</div>
          </Section>

          <Section label={<span>🌐 External guests · one-time email invite ({guestInvites.length})</span>}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-2">
              For non-hubCMD attendees — auditors, contractors, customers. They get a signed 7-day link, no account required.
            </div>
            <div className="flex gap-2">
              <input data-testid="mtg-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)}
                placeholder="Guest name" className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              <input data-testid="mtg-guest-email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="guest@example.com" className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              <button type="button" data-testid="mtg-guest-add" onClick={() => {
                if (!guestEmail.includes("@")) return;
                setGuestInvites([...guestInvites, { name: guestName || guestEmail, email: guestEmail.trim().toLowerCase() }]);
                setGuestName(""); setGuestEmail("");
              }} className="btn-pill btn-pill-secondary text-[11px]">+ Add</button>
            </div>
            {guestInvites.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mtg-guest-list">
                {guestInvites.map((g, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-[#1a1a1a] text-white text-[11px] px-2.5 py-1 rounded-full" data-testid={`mtg-guest-chip-${i}`}>
                    {g.name} <span className="text-white/50">·</span> <span className="text-white/70">{g.email}</span>
                    <button onClick={() => setGuestInvites(guestInvites.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </Section>

          {startNow ? (
            <div className="rounded-xl bg-[#fff1ea] border border-[#ff4500]/30 p-3 flex items-start gap-2" data-testid="mtg-live-warning">
              <AlertTriangle className="w-4 h-4 text-[#ff4500] mt-0.5 flex-shrink-0" />
              <div className="text-[12px] text-[#1a1a1a]">
                <strong>Heads up — this will go live immediately.</strong> Attendees will get an email invite with a live &ldquo;Attend Meeting&rdquo; link. Make sure your call is running before you create.
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-[#e8f5ee] border border-[#1a8754]/25 p-3 flex items-start gap-2" data-testid="mtg-scheduled-note">
              <CalendarClock className="w-4 h-4 text-[#1a8754] mt-0.5 flex-shrink-0" />
              <div className="text-[12px] text-[#1a1a1a]">
                <strong>Meeting will be scheduled for {scheduledFor ? new Date(scheduledFor).toLocaleString() : "—"}.</strong> No email invites go out yet — hit &ldquo;Start meeting&rdquo; on the meeting card when it&apos;s time to go live.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 bg-white border-t border-[#1a1a1a]/10 px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            {actCount} action{actCount === 1 ? "" : "s"} · {attCount} attendee{attCount === 1 ? "" : "s"}
          </div>
          <button data-testid="mtg-generate-btn" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary">
            {busy ? "Saving…" : startNow ? "Create & Start" : "Create meeting"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1.5 font-mono">{label}</label>
      {children}
    </div>
  );
}

void X;
void useMemo;
